/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { useCallback, useEffect, useState } from "react";
import { useTranslation, untranslated } from "@i18n";
import {
  getEditBOQItemsTabs,
  fetchTakeoffsListAttachedToItem,
  getMeasurementUnits,
  saveItemInEstimate,
  isEstimateRequiredPermission,
} from "@Actions/estimation";
import { Box, Tab, Tabs } from "@mui/material";
import BoqItemBasicDetails from "./BoqItemBasicDetails";
import BoqItemQuantityDetails from "./BoqItemQuantityDetails";
import type { TakeoffSelectionRow } from "./BoqItemQuantityDetails";
import BoqItemRateDetails from "./BoqItemRateDetails";
import BoqItemUnitDetails from "./BoqItemUnitDetails";
import BoqItemAmountDetails from "./BoqItemAmountDetails";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { ADDON_CODE_TO_LAYOUT_FLAG, folderPermissions, RATE_ANALYSIS_ADDON_CODE } from "@Helpers/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { useParams } from "react-router-dom";
import useLayoutModuleAccess from "@Services/useLayoutModuleAccess";
import type { RootState } from "src/reducers";
import type {
  EstimateCurrency,
  EstimateData as EstimateDataState,
  GetEstimateItemsRequestValue,
} from "@ServerTypes/estimates/estimates.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type CurrencyDetails = EstimateCurrency;

// ADDED: rate analysis row shape - the backend stores this as `z.unknown()` in
// `saveItemDetailsRequest.rateAnalysis` (legacy array/object format). The component
// only relies on `id` for identity tracking; the rest is preserved as-is.
type RateAnalysisRow = {
  id: string | null;
  [key: string]: unknown;
};

type RateAnalysisData = {
  data: RateAnalysisRow[];
  total: number;
  say: number;
};

type RateAnalysisPayload = {
  rateAnalysisData: RateAnalysisData;
  purchaseUnitCount: number;
};

type ItemCurrentDetails = {
  code?: string;
  description?: string;
  purchaseUnit?: ObjectIdString;
  purchaseUnitName?: string;
  measurementUnit?: ObjectIdString;
  measurementUnitName?: string;
  conversionRatePU?: string;
  conversionRateMU?: string;
  manualQuantity?: number;
  quantityFromTakeoffs?: number;
  rate?: number;
  amount?: number;
  additionalAmount?: number | string;
  hasChild?: boolean;
  itemId?: ObjectIdString;
  rateAnalysis: RateAnalysisPayload;
  isRateAnalysisEnable?: boolean;
};

type BoqItemDetails = {
  code?: string;
  description?: string;
  purchaseUnit?: { id?: ObjectIdString; name?: string };
  measurementUnit?: { id?: ObjectIdString; name?: string; relation?: string };
  manualQuantity?: number;
  quantityFromTakeoffs?: number;
  rate?: number | null;
  amount?: number;
  additionalAmount?: number | string;
  hasChild?: boolean;
  itemId?: ObjectIdString;
  rateAnalysis?: RateAnalysisPayload;
  isRateAnalysisEnable?: boolean;
};

type BoqItemEditPopUpProps = {
  closeEditModalPopUp: () => void;
  itemDetails: BoqItemDetails;
  currencyDetails: CurrencyDetails;
  refreshCallBack: (data: BoqItemDetails) => void;
};

// ADDED: derived from server schema GetMeasurementUnitsResponse.result item; the API
// also returns `nameWithSymbol` and `defaultMeasurementUnit*` projected fields used in
// the picker. The base shape already covers `_id`, `type`, `selectedSymbol`.
type MeasurementUnitOption = NonNullable<
  import("@ServerTypes/units/units.schema").GetMeasurementUnitsResponse["result"]
>[number] & {
  _id: string;
  nameWithSymbol: string;
  type: { name: string; id: string };
  /**
   * Projected from `defaultMeasurementUnit._id`; declared `objectIdString` everywhere it appears
   * in units.schema.ts (purchase-unit request/response and getUnitTypes), so it is branded here
   * rather than re-declared as a plain `string`.
   */
  defaultMeasurementUnitId?: ObjectIdString;
  defaultMeasurementUnit?: string;
  defaultConversionRate?: string;
};

/**
 * One node of the UNIT tab's tree picker: a measurement-unit TYPE grouping its units. `_id` is the
 * unit type's id (`MeasurementUnitOption["type"]["id"]`, an `objectIdString` in units.schema.ts), so
 * it must not be re-declared as a plain `string` — `BoqItemUnitDetails` takes branded `_id`s.
 */
type MeasurementUnitTypeGroup = {
  _id: MeasurementUnitOption["type"]["id"];
  name: string;
  selectable: boolean;
  units: Array<{ _id: string; name: string; type: MeasurementUnitOption["type"] }>;
};

const BoqItemEditPopUp = ({
  closeEditModalPopUp,
  itemDetails,
  currencyDetails,
  refreshCallBack,
}: BoqItemEditPopUpProps) => {
  const { t } = useTranslation();
  const EditBOQItemsTabs = getEditBOQItemsTabs() as {
    [k in "DETAILS" | "QUANTITY" | "UNIT" | "RATE" | "AMOUNT"]: { value: string; text?: string };
  };
  const [buttonLoading, setButtonLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(EditBOQItemsTabs.DETAILS?.value ?? "DETAILS");
  const [attachedTakeoffsList, setAttachedTakeoffsList] = useState<TakeoffSelectionRow[]>([]);
  const [measurementUnitsDataSource, setMeasurementUnitsDataSource] = useState<MeasurementUnitTypeGroup[]>([]);
  const normalizedRateAnalysis: RateAnalysisPayload = itemDetails.rateAnalysis
    ? {
        ...itemDetails.rateAnalysis,
        rateAnalysisData: {
          ...itemDetails.rateAnalysis.rateAnalysisData,
          data: (itemDetails.rateAnalysis.rateAnalysisData.data ?? []).map((row: RateAnalysisRow) => ({
            ...row,
            id: row.id ?? null,
          })),
        },
        purchaseUnitCount: itemDetails.rateAnalysis.purchaseUnitCount ?? 1,
      }
    : { rateAnalysisData: { data: [], total: 0, say: 0 }, purchaseUnitCount: 1 };
  const [itemCurrentDetails, setItemCurrentDetails] = useState<ItemCurrentDetails>({
    code: itemDetails.code,
    description: itemDetails.description,
    purchaseUnit: !itemDetails.hasChild ? _.get(itemDetails, "purchaseUnit.id") : undefined,
    purchaseUnitName: !itemDetails.hasChild ? _.get(itemDetails, "purchaseUnit.name") : "",
    measurementUnit: !itemDetails.hasChild ? _.get(itemDetails, "measurementUnit.id") : undefined,
    measurementUnitName: !itemDetails.hasChild ? _.get(itemDetails, "measurementUnit.name") : "",
    conversionRatePU: itemDetails.measurementUnit?.relation?.split(":")[0],
    conversionRateMU: itemDetails.measurementUnit?.relation?.split(":")[1],
    manualQuantity: itemDetails.manualQuantity ?? undefined,
    quantityFromTakeoffs: itemDetails.quantityFromTakeoffs ?? undefined,
    rate: !itemDetails.hasChild ? (itemDetails.rate ?? undefined) : undefined,
    amount: itemDetails.amount,
    additionalAmount: itemDetails.additionalAmount,
    hasChild: itemDetails.hasChild,
    itemId: itemDetails.itemId,
    rateAnalysis: normalizedRateAnalysis,
    isRateAnalysisEnable: !itemDetails.hasChild ? itemDetails.isRateAnalysisEnable : false,
  });

  const addonsAccess = useLayoutModuleAccess(ADDON_CODE_TO_LAYOUT_FLAG[RATE_ANALYSIS_ADDON_CODE]);

  const { RESOURCE_MODIFY } = folderPermissions();
  // Route params are the external entry point for these ids; the backend route schema validates them.
  const params = useParams<{ estimateId: ObjectIdString; versionId: ObjectIdString }>();

  const estimateData = useSelector((store: RootState) => store.estimateData) as EstimateDataState;
  const dispatch = useDispatch();
  const [isActionPermission] = useState(isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id));

  const validateItemDetails = () => {
    let content;
    const manualQuantity = itemCurrentDetails.manualQuantity ?? 0;
    const rateValue = itemCurrentDetails.rate ?? 0;
    const conversionRateMU = Number(itemCurrentDetails.conversionRateMU);
    const conversionRatePU = Number(itemCurrentDetails.conversionRatePU);
    const additionalAmount = itemCurrentDetails.additionalAmount ?? 0;

    if (manualQuantity < 0) {
      content = t("Please enter a positive value for the quantity");
      return { status: false, content };
    }

    if (Number(additionalAmount) < 0) {
      content = t("Please enter a positive value for the Additional Amount");
      return { status: false, content };
    }

    if (
      !itemCurrentDetails.code &&
      manualQuantity &&
      (!itemCurrentDetails.purchaseUnit || itemCurrentDetails.rate === null)
    ) {
      content = t("Please specify the unit and rate for the item");
      return { status: false, content };
    }

    if (!itemCurrentDetails.code && itemCurrentDetails.purchaseUnit && itemCurrentDetails.rate === null) {
      content = t("Please enter the value of rate");
      return { status: false, content };
    }

    if (!itemCurrentDetails.code && !itemCurrentDetails.purchaseUnit && itemCurrentDetails.rate) {
      content = t("Please enter the value of unit");
      return { status: false, content };
    }

    if (!itemCurrentDetails.description) {
      content = t("Please enter the description for the item");
      return { status: false, content };
    }

    if (!itemCurrentDetails.code && rateValue < 0) {
      content = t("Please enter a positive value for rate");
      return { status: false, content };
    }

    if (!itemCurrentDetails.hasChild && itemCurrentDetails.purchaseUnit) {
      if (Number.isNaN(conversionRateMU) || conversionRateMU <= 0) {
        content = t("Please enter a positive value for relation");
        return { status: false, content };
      }

      if (Number.isNaN(conversionRatePU) || conversionRatePU <= 0) {
        content = t("Please enter a positive value for relation");
        return { status: false, content };
      }
    }

    return { status: true, content: undefined };
  };

  const generatePayload = () => {
    const additionalAmountValue = Number(itemCurrentDetails.additionalAmount);
    let data: NonNullable<GetEstimateItemsRequestValue> = {
      additionalAmount: Number.isNaN(additionalAmountValue) ? null : additionalAmountValue,
      itemId: itemCurrentDetails.itemId,
    };

    if (!itemCurrentDetails.hasChild) {
      data = {
        ...data,
        quantity: itemCurrentDetails.manualQuantity,
        measurementUnit: itemCurrentDetails.measurementUnit,
      };
      if (itemCurrentDetails.purchaseUnit) {
        const conversionRatePUValue = Number(itemCurrentDetails.conversionRatePU);
        const conversionRateMUValue = Number(itemCurrentDetails.conversionRateMU);
        const conversionRatePU = Number.isNaN(conversionRatePUValue) ? 1 : conversionRatePUValue;
        const conversionRateMU = Number.isNaN(conversionRateMUValue) ? 1 : conversionRateMUValue;
        data.conversionRate = `${conversionRatePU}:${conversionRateMU}`;
      }
    }

    if (!itemCurrentDetails.code) {
      data.description = itemCurrentDetails.description;
      if (!itemCurrentDetails.hasChild) {
        data.rate = itemCurrentDetails.rate;

        if (addonsAccess.allowed) {
          data = {
            ...data,
            rateAnalysis: itemCurrentDetails.isRateAnalysisEnable ? itemCurrentDetails.rateAnalysis : null,
            isRateAnalysisEnable: itemCurrentDetails.isRateAnalysisEnable,
          };
        }

        if (!Number.isFinite(itemCurrentDetails.quantityFromTakeoffs)) {
          data.purchaseUnit = itemCurrentDetails.purchaseUnit;
        }
      }
    }

    return {
      value: data,
      params: {
        estimateId: params.estimateId,
      },
      action: "update" as const,
    };
  };

  const saveEstimateItemDetails = async () => {
    setButtonLoading(true);

    const validation = validateItemDetails();
    if (!validation.status && validation.content) {
      dispatch(showToastNotification({ content: validation.content, type: TOAST_TYPE_ERROR }));
      setButtonLoading(false);
      return;
    }
    const payload = generatePayload();
    const response = await saveItemInEstimate(payload);
    if (response.data.status) {
      dispatch(showToastNotification({ content: t("Item has been saved successfully"), type: TOAST_TYPE_SUCCESS }));
      refreshCallBack(response.data.result as Parameters<typeof refreshCallBack>[0]);
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(response.data.errorMessage) || SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setButtonLoading(false);
  };

  const fetchTakeoffsList = useCallback(async () => {
    if (!itemCurrentDetails.itemId || !params.estimateId) return;
    const response = await fetchTakeoffsListAttachedToItem({
      itemId: itemCurrentDetails.itemId,
      versionId: params.versionId,
      estimateId: params.estimateId,
    });

    if (response.data.status && response.data.result) {
      setAttachedTakeoffsList(response.data.result);
    }
  }, [itemCurrentDetails.itemId, params.estimateId, params.versionId]);

  useEffect(() => {
    if (!itemCurrentDetails.hasChild) {
      fetchTakeoffsList();
    }
  }, [fetchTakeoffsList, itemCurrentDetails.hasChild]);

  // ADDED note: MeasurementUnitOption is declared above the component to be visible
  // in the `useState` generic for `measurementUnitsDataSource`.
  const refactorUnitsData = (data: MeasurementUnitOption[]) => {
    const unitsWithTypeMap: {
      [k: string]: Array<{ _id: string; name: string; type: MeasurementUnitOption["type"] }>;
    } = {};
    data.forEach((unit: MeasurementUnitOption) => {
      const unitData = {
        _id: unit._id,
        name: unit.nameWithSymbol,
        type: unit.type,
      };
      const bucket = unitsWithTypeMap[unit.type.name] ?? [];
      if (!unitsWithTypeMap[unit.type.name]) {
        unitsWithTypeMap[unit.type.name] = bucket;
      }
      bucket.push(unitData);
    });

    return Object.keys(unitsWithTypeMap)
      .map((typeName: string) => {
        const firstUnit = unitsWithTypeMap[typeName]?.[0];
        if (!firstUnit) {
          return null;
        }
        return {
          _id: firstUnit.type.id,
          name: typeName,
          selectable: false,
          units: unitsWithTypeMap[typeName] ?? [],
        };
      })
      .filter((item): item is MeasurementUnitTypeGroup => Boolean(item));
  };

  const getUnits = async () => {
    const response = await getMeasurementUnits({});
    if (response.data.status) {
      setMeasurementUnitsDataSource(refactorUnitsData((response.data.result ?? []) as MeasurementUnitOption[]));
    } else {
      dispatch(showToastNotification({ content: t("Unable to fetch Measurment Units"), type: TOAST_TYPE_ERROR }));
      closeEditModalPopUp();
    }
  };

  useEffect(() => {
    getUnits();
  }, []);

  const getLatestAmount = (rate = 0, manualQuantity = 0, quantityFromTakeoffs = 0) => {
    return Number(rate) * (manualQuantity + quantityFromTakeoffs);
  };

  // const generateQuantityFromTakeoffs = (currentData)=>{
  //   if(_.get(itemDetails, "measurementUnit.relation")){
  //     const intialConversionRatePU = parseFloat(itemDetails.measurementUnit.relation.split(":")[0]) || 1
  //     const intialConversionRateMU = parseFloat(itemDetails.measurementUnit.relation.split(":")[1]) || 1

  //     let currentConversionRatePU = parseFloat(currentData.conversionRatePU) || 1
  //     let currentConversionRateMU = parseFloat(currentData.conversionRateMU) || 1

  //     return ((currentData.quantityFromTakeoffs) *(currentConversionRateMU/currentConversionRatePU) *(intialConversionRatePU/intialConversionRateMU))
  //   }else{
  //     return currentData.quantityFromTakeoffs
  //   }
  // }

  type UpdateArgs =
    | {
        target: {
          value: string;
          itemData?: {
            name?: string;
            defaultMeasurementUnitId?: string;
            defaultMeasurementUnit?: string;
            defaultConversionRate?: string;
          };
          name?: string;
        };
      }
    | { itemData: { id: string } }
    | RateAnalysisPayload;

  // ADDED: updateItemDetails accepts a polymorphic `args` from multiple callers
  // (form input change, Cost Database row select, rate analysis save). The narrow guards
  // below keep each branch type-safe without introducing a wider input shape.
  type UpdateItemDetailsInput =
    | Extract<UpdateArgs, { target: unknown }>
    | Extract<UpdateArgs, { itemData: unknown }>
    | RateAnalysisPayload
    | { value: { _id?: string; name?: string } & Partial<MeasurementUnitOption> };
  const updateItemDetails = (args: UpdateItemDetailsInput, field?: string, checkNumberIsPositive = false) => {
    const hasTarget = (value: UpdateItemDetailsInput): value is Extract<UpdateArgs, { target: unknown }> =>
      typeof value === "object" && value !== null && "target" in value;
    const hasItemData = (value: UpdateItemDetailsInput): value is Extract<UpdateArgs, { itemData: unknown }> =>
      typeof value === "object" && value !== null && "itemData" in value;
    const hasRateAnalysisData = (value: UpdateItemDetailsInput): value is RateAnalysisPayload =>
      typeof value === "object" && value !== null && "rateAnalysisData" in value;
    if (field && ["rate", "manualQuantity"].includes(field)) {
      const target = hasTarget(args) ? args.target : { value: "" };
      let fieldValue = Number(target.value);
      if (Number.isNaN(fieldValue) || (checkNumberIsPositive && fieldValue < 0)) {
        target.value = "";
        fieldValue = NaN;
      }
      setItemCurrentDetails((prev) => {
        const next = { ...prev, [field]: Number.isNaN(fieldValue) ? null : fieldValue };
        return { ...next, amount: getLatestAmount(next.rate, next.manualQuantity, next.quantityFromTakeoffs) };
      });
    } else if (field === "purchaseUnit") {
      setItemCurrentDetails((prev) => {
        const unit = _.get(args, "value") as
          | (Partial<MeasurementUnitOption> & { _id?: string; name?: string })
          | undefined;
        if (unit) {
          prev = { ...prev, [field]: unit._id, purchaseUnitName: unit.name };
          if (!prev.measurementUnit) {
            prev = {
              ...prev,
              measurementUnit: unit?.defaultMeasurementUnitId,
              measurementUnitName: unit?.defaultMeasurementUnit,
              conversionRatePU: unit?.defaultConversionRate?.split(":")[0],
              conversionRateMU: unit?.defaultConversionRate?.split(":")[1],
            };
          }
        }

        return prev;
      });
    } else if (field == "measurementUnit") {
      // The picked row is a MeasurementUnits document, whose `_id`/`id` are `objectIdString` in
      // units.schema.ts — the same brand `ItemCurrentDetails.measurementUnit` carries.
      const itemData = (hasItemData(args) ? args.itemData : { _id: "", id: "" }) as {
        _id?: ObjectIdString;
        id?: ObjectIdString;
      };
      setItemCurrentDetails((prev) => ({ ...prev, [field]: itemData._id ?? itemData.id }));
    } else if (field === "isRateAnalysisEnable") {
      setItemCurrentDetails((prev) => {
        const shouldEnable = !prev.isRateAnalysisEnable;

        return {
          ...prev,
          rate: shouldEnable ? prev.rateAnalysis.rateAnalysisData.say : prev.rate,
          [field]: shouldEnable,
        };
      });
    } else if (field === "rateAnalysis") {
      const rateAnalysisArgs: RateAnalysisPayload = hasRateAnalysisData(args)
        ? args
        : { rateAnalysisData: { say: 0, total: 0, data: [] }, purchaseUnitCount: 1 };
      setItemCurrentDetails((prev) => ({
        ...prev,
        rateAnalysis: {
          ...prev.rateAnalysis,
          rateAnalysisData: {
            ...prev.rateAnalysis.rateAnalysisData,
            ...rateAnalysisArgs.rateAnalysisData,
            data: (rateAnalysisArgs.rateAnalysisData.data ?? []).map((row: RateAnalysisRow) => ({
              ...row,
              id: row.id ?? null,
            })),
          },
          purchaseUnitCount: rateAnalysisArgs.purchaseUnitCount ?? prev.rateAnalysis.purchaseUnitCount,
        },
        rate: rateAnalysisArgs.rateAnalysisData.say,
      }));
    } else {
      const target = hasTarget(args) ? args.target : { value: "", name: "" };
      let fieldValue = target.value;
      if (checkNumberIsPositive) {
        const numericValue = Number(fieldValue);
        if (!Number.isNaN(numericValue) && numericValue < 0) {
          fieldValue = "";
          target.value = "";
        }
      }
      const fieldName = field || target.name;
      if (fieldName) {
        setItemCurrentDetails((prev) => ({ ...prev, [fieldName]: fieldValue }));
      }
    }
  };

  const selectedTabContent = () => {
    // `updateItemDetails` is a polymorphic dispatcher (see `UpdateItemDetailsInput` above);
    // each child declares its own per-call-site param shape. We narrow at each child
    // boundary via separate casts so that:
    //  - Children with strict unions (BoqItemBasic/Rate/Unit/Amount) get the narrowed shape
    //  - BoqItemQuantityDetails (out of this refactor's scope) still receives `unknown`
    const commonPropsBase = {
      itemCurrentDetails,
      currencyDetails,
      isActionPermission,
      attachedTakeoffsList,
    };
    if (currentTab == EditBOQItemsTabs.DETAILS.value) {
      return (
        <BoqItemBasicDetails
          {...commonPropsBase}
          updateItemDetails={updateItemDetails as Parameters<typeof BoqItemBasicDetails>[0]["updateItemDetails"]}
          setCurrentTab={setCurrentTab}
        />
      );
    } else if (currentTab == EditBOQItemsTabs.QUANTITY.value) {
      return (
        <BoqItemQuantityDetails
          {...commonPropsBase}
          updateItemDetails={updateItemDetails as Parameters<typeof BoqItemQuantityDetails>[0]["updateItemDetails"]}
        />
      );
    } else if (currentTab == EditBOQItemsTabs.UNIT.value) {
      return (
        <BoqItemUnitDetails
          {...commonPropsBase}
          updateItemDetails={updateItemDetails as Parameters<typeof BoqItemUnitDetails>[0]["updateItemDetails"]}
          measurementUnitsDataSource={measurementUnitsDataSource}
        />
      );
    } else if (currentTab == EditBOQItemsTabs.RATE.value) {
      return (
        <BoqItemRateDetails
          {...commonPropsBase}
          updateItemDetails={updateItemDetails as Parameters<typeof BoqItemRateDetails>[0]["updateItemDetails"]}
        />
      );
    } else if (currentTab == EditBOQItemsTabs.AMOUNT.value) {
      return (
        <BoqItemAmountDetails
          {...commonPropsBase}
          updateItemDetails={updateItemDetails as Parameters<typeof BoqItemAmountDetails>[0]["updateItemDetails"]}
        />
      );
    }
  };

  const content = () => (
    <div className="h-full flex flex-col">
      <div className="px-8 border-0 border-b border-button_light">
        <Box sx={{ paddingBottom: "0.1875rem" }}>
          <Tabs
            value={currentTab}
            onChange={(event, selectedTab) => setCurrentTab(selectedTab)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "#6BBBE3",
                height: "0.25rem",
                borderRadius: "1.25rem",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "1rem",
                color: "#192048",
                minWidth: "auto",
                marginInlineEnd: "3rem",
                padding: 0,
              },
              "& .Mui-selected": {
                color: "#192048",
                fontWeight: 600,
              },
            }}
          >
            {(
              Object.entries(EditBOQItemsTabs) as Array<
                [keyof typeof EditBOQItemsTabs, { value: string; text?: string }]
              >
            ).map(([tabKey, tab]) => (
              <Tab label={tab.text} value={tabKey} key={String(tabKey)} />
            ))}
          </Tabs>
        </Box>
      </div>
      <div className="px-8 flex w-full h-full border-0 border-b border-button_light">{selectedTabContent()}</div>
    </div>
  );

  const footer = () => (
    <div className="flex gap-4 px-8 py-3">
      <ButtonComponent
        onClick={saveEstimateItemDetails}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        loading={buttonLoading}
      >
        {t("Confirm")}
      </ButtonComponent>
      <ButtonComponent
        onClick={closeEditModalPopUp}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        loading={buttonLoading}
      >
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      footerContent={footer()}
      headerTitle={<span className="px-8 py-3">{t("Edit Item")}</span>}
      isVisible
      onCancel={closeEditModalPopUp}
      modelContent={content()}
      width="80%"
      height="95%"
      modalPadding={0}
    />
  );
};

export default BoqItemEditPopUp;
